import {
  Box,
  Paper,
  TextField,
  IconButton,
  List,
  ListItem,
} from '@mui/material';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useChatStream } from '../../hooks/useChatStream';
import { IMessages } from '../../interface/message';
import styles from './styles.module.scss';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { ASSETS } from '../../const/assets';

function ChatScreen() {
  const [messages, setMessages] = useState<IMessages[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [openModal, setOpenModal] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);

  const { sendMessage } = useChatStream();

  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleChunkCallback = useCallback((chunk: string) => {
    setMessages((prevMessages) => {
      const newMessages = structuredClone(prevMessages);
      const lastMessage = newMessages[newMessages.length - 1];
      if (!lastMessage.content.endsWith(chunk)) {
        lastMessage.content = lastMessage.content + chunk;
      }
      return newMessages;
    });
  }, []);

  const sendMessageWithSuggestion = (suggestion: string) => {
    setMessages([
      ...messages,
      {
        role: 'user',
        content: suggestion,
      },
      {
        role: 'assistant',
        content: '',
      },
    ]);
    setNewMessage('');
    sendMessage(
      {
        senderTitle: 'vqg_bavi',
        messages: [{ role: 'user', content: suggestion }],
      },
      handleChunkCallback
    );
  };

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      setMessages([
        ...messages,
        {
          role: 'user',
          content: newMessage,
        },
        {
          role: 'assistant',
          content: '',
        },
      ]);
      setNewMessage('');

      sendMessage(
        {
          senderTitle: 'vqg_bavi',
          messages: [{ role: 'user', content: newMessage }],
        },
        handleChunkCallback
      );
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    // Function to update the window height
    const updateHeight = () => {
      setWindowHeight(window.innerHeight);
    };

    // Add event listeners
    window.addEventListener('resize', updateHeight);

    // For mobile browsers, also listen for orientation changes
    window.addEventListener('orientationchange', updateHeight);

    // Initial setup
    updateHeight();

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('orientationchange', updateHeight);
    };
  }, []);

  return (
    <>
      <div
        className={`flex items-center justify-center relative ${
          isFormOpen ? 'md:justify-end' : 'md:justify-start'
        }`}
        style={{ height: `${windowHeight}px` }}
      >
        <img
          className="absolute z-1 w-[105px] h-[154px] top-10 md:left-10 left-0 right-0 m-auto md:right-[unset]"
          src={ASSETS.icLogo1}
          alt=""
        />
        <img
          className="absolute z-1 md:w-[700px] md:h-[300px] w-[300px] top-0 left-0 bottom-0 right-0 m-auto md:mt-60 mt-70 object-cover"
          src={ASSETS.icText}
          alt=""
        />
        <img
          className="md:w-screen hidden md:block md:absolute left-0 top-0 md:h-screen object-cover"
          src={!isFormOpen ? ASSETS.bgChat : ASSETS.bsm}
          alt="bgChat"
        />
        <img
          className="md:hidden absolute left-0 top-0 h-screen object-cover w-screen"
          src={ASSETS.bgMobile}
          alt="bgChat"
        />
        {!openModal && (
          <div className="flex gap-4 rounded-t-2xl w-full fixed bottom-0 justify-end">
            <div
              className="z-50 justify-end p-3 md:p-2 md:rounded-full cursor-pointer md:fixed md:right-16 md:bottom-1 lg:bottom-5 md:w-auto"
              onClick={handleOpenModal}
            >
              <img src={ASSETS.icHD} alt="icHD" className="w-14 h-14" />
            </div>
          </div>
        )}

        <div
          className={`fixed md:bottom-16 md:right-16 transition-all duration-300 z-50 ${
            openModal ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
          }`}
          style={{ transformOrigin: 'bottom right' }}
        >
          <Box
            sx={{
              width: { xs: '100vw', md: '420px' },
              height: {
                xs: `${windowHeight}px`,
                md: '85vh',
              },
              backgroundColor: 'white',
              borderRadius: { xs: '0', md: 10 },
              boxShadow: 3,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div className="pt-8 pb-5 border-[#ECECEC] border-b px-6 bg-linear-to-r from-[#4ECBD4] to-[#4E9BD9] md:rounded-t-[40px]">
              <div className="flex items-center gap-4">
                <img
                  src={ASSETS.icBack}
                  alt="icBack"
                  className="w-6 h-6 cursor-pointer"
                  onClick={handleCloseModal}
                />
                <div className="flex-1 flex items-center gap-4">
                  <img src={ASSETS.icHD} alt="icHDV" className="w-11 h-11" />
                  <div>
                    <span className="text-white font-bold text-xl">
                      Hướng dẫn viên AI
                    </span>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-white rounded-full" />
                      <span className="text-white text-sm font-bold">
                        Online
                      </span>
                    </div>
                  </div>
                </div>
                <img src={ASSETS.icLoa} alt="icLoa" className="w-6 h-6" />
              </div>
            </div>
            <div className="flex-1 mb-2 sm:mb-4 overflow-auto p-2 sm:p-4">
              {messages.length === 0 && (
                <div className="flex items-center justify-center flex-col">
                  <img src={ASSETS.icM} alt="icM" className="w-7 h-7" />
                  <span className="text-sm mt-1 font-bold">
                    Bạn đang quan tâm?
                  </span>
                  <div
                    className="text-sm mt-4 bg-[#F4F4F4] rounded-3xl py-2 flex items-center justify-center w-full cursor-pointer"
                    onClick={() =>
                      sendMessageWithSuggestion(
                        'Bạn muốn được gợi ý lịch trình tham quan?'
                      )
                    }
                  >
                    Bạn muốn được gợi ý lịch trình tham quan?
                  </div>
                  <div
                    className="text-sm bg-[#F4F4F4] rounded-3xl my-2 py-2 flex items-center justify-center w-full cursor-pointer"
                    onClick={() =>
                      sendMessageWithSuggestion(
                        'Bạn muốn tìm hiểu lịch trình trải nghiệm?'
                      )
                    }
                  >
                    Bạn muốn tìm hiểu lịch trình trải nghiệm?
                  </div>
                  <div
                    className="text-sm bg-[#F4F4F4] rounded-3xl py-2 flex items-center justify-center w-full cursor-pointer"
                    onClick={() =>
                      sendMessageWithSuggestion(
                        'Bạn muốn đặt dịch vụ tại C400 town?'
                      )
                    }
                  >
                    Bạn muốn đặt dịch vụ tại C400 town?
                  </div>
                </div>
              )}
              <List>
                {messages.map((message, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      justifyContent:
                        message.role === 'user' ? 'flex-end' : 'flex-start',
                      position: 'relative',
                    }}
                  >
                    <Paper
                      elevation={1}
                      sx={{
                        p: { xs: 1, sm: 1.5 },
                        backgroundColor:
                          message.role === 'user' ? '#4E9BD9' : '#EEEEEE',
                        maxWidth: '90%',
                        wordBreak: 'break-word',
                        color:
                          message.role === 'user'
                            ? 'white'
                            : message.content === 'Biểu mẫu'
                            ? '#0045BF'
                            : '#434343',
                        borderRadius:
                          message.role === 'user'
                            ? '25px 25px 0 25px'
                            : '25px 25px 25px 0',
                        margin:
                          message.role === 'user' ? '0px' : '0 0 15px 15px',
                        cursor:
                          message.content === 'Biểu mẫu'
                            ? 'pointer'
                            : 'default',
                      }}
                      onClick={() => {
                        if (message.content === 'Biểu mẫu') {
                          setIsFormOpen(true);
                          handleCloseModal();
                        }
                      }}
                    >
                      <div>
                        {message.role === 'assistant' &&
                        message.content === '' ? (
                          <span style={{ marginLeft: 4 }}>
                            <span className={styles.loadingDot}></span>
                            <span className={styles.loadingDot}></span>
                            <span className={styles.loadingDot}></span>
                          </span>
                        ) : (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm, remarkMath]}
                            rehypePlugins={[rehypeKatex]}
                            components={{
                              code: ({ children, ...props }) => {
                                return (
                                  <code
                                    className={`${styles.inlineCode} ${props.className}`}
                                    {...props}
                                  >
                                    {children}
                                  </code>
                                );
                              },
                            }}
                          >
                            {message.content}
                          </ReactMarkdown>
                        )}
                      </div>
                    </Paper>
                    {message.role !== 'user' && (
                      <div
                        className="absolute -bottom-2 left-0 p-1 bg-white rounded-full"
                        style={{
                          boxShadow: '0px 1.96px 1.96px 0px #0000000D',
                        }}
                      >
                        <img
                          src={ASSETS.icHD}
                          alt="AI Avatar"
                          className="w-6 h-6"
                        />
                      </div>
                    )}
                  </ListItem>
                ))}
                <div ref={messagesEndRef} />
              </List>
            </div>

            <Box
              sx={{
                display: 'flex',
                gap: { xs: 0.5, sm: 1 },
                px: { xs: 0, sm: 4 },
                pb: { xs: 0, sm: 4 },
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Xin chào! Tôi có thể giúp gì cho bạn?"
                className="text-[#3E3E3E]"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSendMessage();
                  }
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim()}
                        sx={{
                          width: 40,
                          height: 40,
                        }}
                      >
                        <img
                          src={
                            newMessage.trim()
                              ? ASSETS.icSend
                              : ASSETS.icSendDisabled
                          }
                          alt="icSend"
                        />
                      </IconButton>
                    ),
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1rem',
                    borderRadius: { xs: '0', sm: '30px' },
                    boxShadow: '5px 4px 20px 0px #00000021',
                    '& fieldset': { border: 'none' },
                    '& input': { paddingLeft: '22px' },
                  },
                  '& .MuiInputBase-root': {
                    paddingRight: '8px',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#4ECBD4',
                    opacity: 1,
                    fontSize: '14px',
                  },
                }}
              />
            </Box>
          </Box>
        </div>
      </div>
    </>
  );
}

export default ChatScreen;
